import { ProctoringAssessmentConfigurationV2 } from "@verity/proctoring-option/types";
import ConfirmationBlock, {
	CommonConfirmationBlockProps,
	ConfirmationBlockSkeleton,
	ConfirmationRow,
	ConfirmationRowDescription,
} from "./ConfirmationBlock";
import { ProctoringDataType } from "../../hooks/state/useSettingsState";
import i18nPackage from "../../../i18n/i18n";
import { Platform } from "@verity/url-matcher";
import { Typography } from "@mui/material";
import styles from "../index.module.css";
import { THIRD_PARTY_IMAGE_MAP } from "../../edit-quiz-steps/StepIconConfig";
import { EllipsisTooltip } from "../../../core";

type ThirdPartyPlatformBlockProps = CommonConfirmationBlockProps & {
	thirdPartyOption: ProctoringAssessmentConfigurationV2["thirdPartyAssessmentOption"];
	thirdPartyPlatforms: ProctoringDataType["thirdPartyPlatforms"];
};

export function ThirdPartyPlatformBlock({
	thirdPartyOption,
	thirdPartyPlatforms,
	loading,
	...props
}: ThirdPartyPlatformBlockProps) {
	const platformId = thirdPartyOption?.thirdPartyPlatformId ? Math.max(0, thirdPartyOption.thirdPartyPlatformId) : 0;
	const platformName =
		thirdPartyPlatforms.find((p) => p.platformId === platformId)?.platformName ??
		i18nPackage.t("editQuizPage.confirmationBlockTSX.defaultPlatform");
	const platformKey = platformId as Platform;
	const quizUrl = thirdPartyOption?.thirdPartyUrl;

	if (loading) return <ConfirmationBlockSkeleton />;

	return (
		<ConfirmationBlock
			{...props}
			variant="compact"
			targetPageName="thirdParty"
			title={i18nPackage.t("editQuizPage.confirmationBlockTSX.thirdPartyPlatform")}
		>
			<ConfirmationRow
				title={
					<EllipsisTooltip entry={quizUrl ?? platformName}>
						{platformKey === Platform.DEFAULT ? (
							<span>
								<ConfirmationRowDescription>{platformName}</ConfirmationRowDescription>
							</span>
						) : (
							<Typography component="span" className={styles.thirdPartyPlatformIcon}>
								{/* If it's a third-party platform, show its image/icon */}
								{THIRD_PARTY_IMAGE_MAP[platformKey]}
							</Typography>
						)}
					</EllipsisTooltip>
				}
			/>
		</ConfirmationBlock>
	);
}

type ThirdPartyPasswordBlockProps = CommonConfirmationBlockProps & {
	thirdPartyOption: ProctoringAssessmentConfigurationV2["thirdPartyAssessmentOption"];
};

export function ThirdPartyPasswordBlock({ thirdPartyOption, loading, ...props }: ThirdPartyPasswordBlockProps) {
	if (loading) return <ConfirmationBlockSkeleton />;
	const hasPassword = !!thirdPartyOption?.password;
	return (
		<ConfirmationBlock
			{...props}
			variant="compact"
			targetPageName="thirdParty"
			title={i18nPackage.t("editQuizPage.confirmationBlockTSX.passwordProtection")}
		>
			<ConfirmationRow description={i18nPackage.t(`editQuizPage.confirmationBlockTSX.${hasPassword ? "yes" : "no"}`)} />
		</ConfirmationBlock>
	);
}

type ThirdPartyQuizNameBlockProps = CommonConfirmationBlockProps & {
	quizName: string;
};

export function ThirdPartyQuizNameBlock({ quizName, loading, ...props }: ThirdPartyQuizNameBlockProps) {
	if (loading) return <ConfirmationBlockSkeleton />;
	return (
		<ConfirmationBlock {...props} title={i18nPackage.t("editQuizPage.confirmationBlockTSX.assessmentName")}>
			<ConfirmationRow description={quizName} tooltipType="overflow" />
		</ConfirmationBlock>
	);
}
